import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard, MousePointer, Monitor, Gamepad2, Command } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

const shortcuts = [
  {
    category: 'Navigation',
    icon: MousePointer,
    items: [
      { keys: ['Double-click'], desc: 'Open program/window' },
      { keys: ['Right-click'], desc: 'Context menu' },
      { keys: ['Drag'], desc: 'Move window' },
      { keys: ['Drag corners'], desc: 'Resize window' },
    ]
  },
  {
    category: 'Keyboard',
    icon: Keyboard,
    items: [
      { keys: ['Alt', 'Tab'], desc: 'Switch windows' },
      { keys: ['F5'], desc: 'Refresh desktop' },
      { keys: ['Esc'], desc: 'Close menu/dialog' },
      { keys: ['?'], desc: 'Show this help' },
    ]
  },
  {
    category: 'Window Controls',
    icon: Monitor,
    items: [
      { keys: ['Double-click title'], desc: 'Maximize/restore' },
      { keys: ['—'], desc: 'Minimize window' },
      { keys: ['□'], desc: 'Maximize window' },
      { keys: ['✕'], desc: 'Close window' },
    ]
  },
  {
    category: 'Easter Eggs',
    icon: Gamepad2,
    items: [
      { keys: ['↑↑↓↓←→←→BA'], desc: 'Konami code 🎮' },
      { keys: ['Type "matrix"'], desc: 'In Command Prompt' },
      { keys: ['Double-click 🔊'], desc: 'Blue Screen!' },
      { keys: ['Wait 2 min'], desc: 'Screen saver' },
    ]
  }
];

export default function KeyboardShortcuts({ isOpen, onClose, onShowTour }) {
  useEffect(() => {
    if (isOpen) {
      playSound(SOUNDS.windowOpen, 0.2);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleShowTour = () => {
    localStorage.removeItem('tourCompleted');
    onClose();
    onShowTour?.();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#c0c0c0',
            border: '3px solid',
            borderColor: '#ffffff #404040 #404040 #ffffff',
            maxWidth: '700px',
            width: '100%',
            boxShadow: '8px 8px 0 rgba(0,0,0,0.3)'
          }}
        >
          {/* Title Bar */}
          <div style={{
            background: 'linear-gradient(90deg, #000080 0%, #1084d0 100%)',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Command size={14} color="white" />
              <span style={{
                color: 'white',
                fontFamily: "'Segoe UI', sans-serif",
                fontSize: '13px',
                fontWeight: 'bold'
              }}>
                Keyboard Shortcuts
              </span>
            </div>
            <button
              onClick={onClose}
              style={{
                width: '20px',
                height: '20px',
                background: '#c0c0c0',
                border: '2px solid',
                borderColor: '#fff #404040 #404040 #fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <X size={12} />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '20px' }}>
            <p style={{
              margin: '0 0 20px 0',
              fontSize: '12px',
              color: '#333',
              fontFamily: "'Segoe UI', sans-serif",
              textAlign: 'center'
            }}>
              Master the Autonomous 95 desktop with these shortcuts!
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '16px'
            }}>
              {shortcuts.map((category, i) => {
                const CategoryIcon = category.icon;
                return (
                  <motion.div
                    key={category.category}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    style={{
                      background: 'white',
                      border: '1px solid #808080',
                      padding: '14px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <CategoryIcon size={16} color="#000080" />
                      <span style={{
                        fontWeight: 'bold',
                        fontSize: '12px',
                        color: '#000080',
                        fontFamily: "'Segoe UI', sans-serif"
                      }}>
                        {category.category}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {category.items.map((item, j) => (
                        <div
                          key={j}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                          }}
                        >
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {item.keys.map((key, k) => (
                              <React.Fragment key={k}>
                                <span style={{
                                  padding: '2px 6px',
                                  background: '#f0f0f0',
                                  border: '1px solid #999',
                                  borderRadius: '3px',
                                  fontSize: '10px',
                                  fontFamily: "'Segoe UI', monospace",
                                  fontWeight: 'bold',
                                  color: '#333',
                                  boxShadow: '0 1px 0 #666'
                                }}>
                                  {key}
                                </span>
                                {k < item.keys.length - 1 && (
                                  <span style={{ 
                                    color: '#999', 
                                    fontSize: '10px',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}>+</span>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                          <span style={{
                            fontSize: '10px',
                            color: '#555',
                            fontFamily: "'Segoe UI', sans-serif",
                            textAlign: 'right',
                            flexShrink: 0
                          }}>
                            {item.desc}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Footer */}
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#f5f5f5',
              border: '1px solid #ccc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                fontSize: '11px',
                color: '#666',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                Press <strong>?</strong> anytime • <strong>ESC</strong> to close
              </span>
              <button
                onClick={handleShowTour}
                style={{
                  padding: '6px 12px',
                  background: '#000080',
                  color: 'white',
                  border: '2px solid',
                  borderColor: '#4040ff #000040 #000040 #4040ff',
                  fontSize: '11px',
                  fontFamily: "'Segoe UI', sans-serif",
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                🎯 Vis Tour Igjen
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
